import { GripVertical, Plus, Save, X } from '@tamagui/lucide-icons';
import { Pressable } from '@unistocks/components/basic/pressable';
import { useParentScrollableRef } from '@unistocks/components/basic/scrollview';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import {
  DraggableItemItemContainer,
  useDraggableItemStyle
} from '@unistocks/components/sortables/draggable-item';
import { H4, Paragraph } from '@unistocks/components/text/text';
import {
  DraggablePortfolioTableColumn,
  TableColumn
} from '@unistocks/features/portfolios/components/portfolio-table/types';
import {
  DEFAULT_PORTFOLIO_COLUMNS,
  DEFAULT_WATCHLIST_COLUMNS,
  useDraggableTableColumns
} from '@unistocks/features/portfolios/hooks/use-table-columns';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { isPortfolio } from '@unistocks/features/portfolios/portfolio-utils';
import { t } from '@unistocks/localization';
import { quicker } from '@unistocks/shared/animations';
import { PRESSABLE_OPACITY } from '@unistocks/shared/consts';
import { Logger } from '@unistocks/shared/services/logger.service';
import { Option } from '@unistocks/types';
import { FormPropsBase } from '@unistocks/types/form-props';
import React, { memo, useCallback, useMemo } from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import Animated, { useAnimatedStyle, withSpring } from 'react-native-reanimated';
import Sortable, {
  CustomHandleProps,
  SortableGridDragEndCallback,
  SortableGridRenderItem,
  useItemContext
} from 'react-native-sortables';
import { z } from 'zod';

const keyExtractor = (item: DraggablePortfolioTableColumn) =>
  item.type === 'header' ? item.key : item.prop;

type PortfolioFormValues = {
  name: string;
  tableColumns: Option<TableColumn>[];
};

export type EditPortfolioFormProps = FormPropsBase<PortfolioFormValues> & {
  portfolio: PortfolioBase;
};

const schemaFactory = () =>
  z.object({
    name: requiredSchema(z.string(), t('::Name')),
    tableColumns: z.array(
      z.object({
        key: z.string(),
        value: z.string()
      })
    )
  });

export const EditPortfolioForm = memo<EditPortfolioFormProps>(
  ({ portfolio, isSubmitting, onSubmit }) => {
    const schema = schemaFactory();
    const defaultColumns = isPortfolio(portfolio)
      ? DEFAULT_PORTFOLIO_COLUMNS
      : DEFAULT_WATCHLIST_COLUMNS;
    const initialValues = useMemo<PortfolioFormValues>(
      () => ({
        name: portfolio.name,
        // String array doesn't work well with hook form field array. use option type instead
        tableColumns: portfolio.settings.tableColumns.length
          ? portfolio.settings.tableColumns.map(col => ({
              key: col,
              value: col as TableColumn
            }))
          : defaultColumns.map(col => ({
              key: col,
              value: col as TableColumn
            }))
      }),
      [defaultColumns, portfolio]
    );

    return (
      <Form
        schema={schema}
        initialValues={initialValues}
        isLoading={isSubmitting}
        triggerIcon={Save}
        onSubmit={onSubmit}
      >
        <Form.Input name={'name'} label={t('::Name')} isRequired />
        <PortfolioTableColumns name="tableColumns" portfolio={portfolio} />
      </Form>
    );
  }
);

EditPortfolioForm.displayName = 'EditPortfolioForm';

type PortfolioTableColumnsProps = {
  name: 'tableColumns';
  form?: Control<PortfolioFormValues>;
  portfolio: PortfolioBase;
};

const PortfolioTableColumns: React.FC<PortfolioTableColumnsProps> = ({ name, form, portfolio }) => {
  const scrollRef = useParentScrollableRef();
  const { fields, append, insert, remove, move } = useFieldArray({ control: form, name });
  const { selected, available } = useDraggableTableColumns(
    fields.map(f => f.value),
    portfolio
  );
  const maxColumnsSelected = selected.length >= 5;

  const items = useMemo<DraggablePortfolioTableColumn[]>(() => {
    return [
      { type: 'header', key: t('::Portfolio:SelectedColumns'), value: 'selected' },
      ...selected,
      { type: 'header', key: t('::Portfolio:AvailableColumns'), value: 'available' },
      ...available
    ];
  }, [selected, available]);

  const renderItem = useCallback<SortableGridRenderItem<DraggablePortfolioTableColumn>>(
    ({ item, index }) => (
      <PortfolioColumnDraggableItem
        mode={
          index === 0
            ? 'fixed-order'
            : item.type === 'column' && (item.isSelected || !maxColumnsSelected)
              ? 'draggable'
              : 'non-draggable'
        }
        item={item}
        onIconPress={() => {
          if (item.type !== 'column') return;

          if (item.isSelected) {
            const fieldIndex = fields.findIndex(f => f.value === item.prop);
            Logger.debug('Removing item:', fieldIndex, item.displayName);
            remove(fieldIndex);
          } else {
            Logger.debug('Adding item:', item.displayName);
            append({ key: item.displayName, value: item.prop });
          }
        }}
      >
        {item.type === 'column' ? (
          <Paragraph flex={1} fw={'$7'}>
            {item.displayName}
          </Paragraph>
        ) : null}
        {item.type === 'header' ? <H4 ml={'$-3'}>{item.key}</H4> : null}
      </PortfolioColumnDraggableItem>
    ),
    [append, fields, maxColumnsSelected, remove]
  );

  const onDragEnd = useCallback<SortableGridDragEndCallback<DraggablePortfolioTableColumn>>(
    ({ data, key, fromIndex, toIndex, keyToIndex }) => {
      const isMovingDown = toIndex > fromIndex;
      // Index 0 is the header
      const movedFromSelected = fromIndex <= fields.length;
      const movedToSelected = isMovingDown
        ? // Dropping above the available columns header
          toIndex <= fields.length
        : // Dropping above or on the available columns header
          toIndex <= fields.length + 1;
      const item = data.at(keyToIndex[key]);
      Logger.debug(
        'Dropped item',
        (item as { displayName: string })?.displayName,
        fromIndex,
        toIndex,
        'movedFromSelected',
        movedFromSelected,
        'movedToSelected',
        movedToSelected
      );

      // For type safety
      if (fromIndex === toIndex || !item || item.type !== 'column') return;

      if (movedFromSelected) {
        if (movedToSelected) {
          // Moving within the selected columns
          Logger.debug('Moved within selected columns:', fromIndex - 1, toIndex - 1);
          move(fromIndex - 1, toIndex - 1);
        } else {
          // Moving from selected to available
          Logger.debug('Moved from selected to available:', fromIndex - 1);
          remove(fromIndex - 1);
        }
      } else if (movedToSelected) {
        Logger.debug('Moved from available to selected:', toIndex - 1);
        // Moving from available to selected
        insert(toIndex - 1, { key: item.displayName, value: item.prop });
      } else {
        // Moving within available columns - do nothing
        Logger.debug('Moved within available columns - no action taken');
      }
    },
    [insert, move, remove, fields.length]
  );

  return (
    <Sortable.Grid
      scrollableRef={scrollRef}
      hapticsEnabled
      showDropIndicator
      customHandle
      bringToFrontWhenActive
      data={items}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      overDrag="vertical"
      activeItemScale={0.9}
      onDragEnd={onDragEnd}
    />
  );
};

type PortfolioColumnDraggableItemProps = CustomHandleProps & {
  item: DraggablePortfolioTableColumn;
  onIconPress: () => void;
};

const PortfolioColumnDraggableItem: React.FC<PortfolioColumnDraggableItemProps> = ({
  children,
  item,
  onIconPress,
  ...props
}) => {
  const { isActive } = useItemContext();
  const style = useDraggableItemStyle();
  const isColumn = item.type === 'column';
  const isHeader = item.type === 'header';
  const isDraggable = props.mode === 'draggable';

  const iconStyle = useAnimatedStyle(() => {
    const isVisible = isColumn && !isActive.value;
    return {
      opacity: withSpring(isVisible ? 1 : 0, quicker),
      transform: [
        {
          translateX: withSpring(isVisible ? 0 : 15, quicker)
        }
      ]
    };
  });

  return (
    <Sortable.Handle {...props}>
      <DraggableItemItemContainer
        gap={'$3'}
        h={'$6'}
        o={isHeader || isDraggable ? 1 : PRESSABLE_OPACITY}
        cursor={isDraggable ? 'grab' : undefined}
        style={style}
      >
        {isColumn ? <GripVertical color={'$unistocks'} /> : null}
        {children}
        {isDraggable && item.type === 'column' ? (
          <Animated.View style={iconStyle}>
            <Pressable withFeedback withScale hitSlop={20} ml={'auto'} onPress={onIconPress}>
              {item.isSelected ? (
                <X size={'$2'} strokeWidth={3} color={'$danger'} />
              ) : (
                <Plus size={'$2'} strokeWidth={3} color={'$success'} />
              )}
            </Pressable>
          </Animated.View>
        ) : null}
      </DraggableItemItemContainer>
    </Sortable.Handle>
  );
};
